import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Sun } from "lucide-react";
import { appConfig, copyrightLine, versionLine } from "@/config/app";
import { InstallerLogo, InstallerContactBlock, installerBranding } from "@/branding/Branding";
import BackButton from "@/components/BackButton";
import { PageContainer } from "@/components/layout/PageLayout";

/**
 * About SolarFlow — hero card with app identity, developer credit,
 * copyright body, installer contact, and links into legal pages.
 * Mirrors mobile app/about.tsx from feature/sync-master.
 */
export default function About() {
    const navigate = useNavigate();
    const b = appConfig.installer;

    return (
        <div className="min-h-full bg-[#F5F5F7] pb-16" data-testid="about-page">
            <PageContainer width="narrow">
                <BackButton testid="about-back" className="mb-4" />

                {/* Hero card */}
                <div className="bg-white rounded-[20px] border border-[#E5E7EB] p-6 md:p-10 text-center shadow-[0_4px_12px_rgba(11,18,32,0.04)]" data-testid="about-app-block">
                    <div className="w-14 h-14 rounded-[16px] bg-[#3E9E2E]/12 flex items-center justify-center mx-auto">
                        <Sun size={26} strokeWidth={1.75} className="text-[#3E9E2E]" />
                    </div>
                    <div className="mt-4 text-xl md:text-2xl text-[#131B2F] font-medium">{appConfig.name}</div>
                    <div className="mt-1 text-sm md:text-base text-[#4B5565]">{appConfig.tagline}</div>
                    <div className="mt-4 inline-block px-3 py-1 rounded-full bg-[#3E9E2E]/12 text-[#3E9E2E] text-xs font-medium tracking-[0.03em]">
                        {versionLine()}
                    </div>
                    <div className="mt-6 flex justify-center gap-10">
                        <Meta label="Build" value={appConfig.buildNumber} />
                        <Meta label="Release" value={appConfig.releaseDate} />
                    </div>
                    <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
                        <div className="text-base text-[#131B2F] font-medium" data-testid="about-developed-by">
                            Developed by {appConfig.copyrightOwner}
                        </div>
                        <div className="mt-1 text-xs text-[#4B5565]">{copyrightLine()}</div>
                    </div>
                </div>

                {/* Statement */}
                <div className="mt-4 bg-white rounded-[20px] border border-[#E5E7EB] p-6" data-testid="about-statement">
                    <p className="text-sm md:text-base text-[#4B5565] leading-relaxed">
                        {appConfig.name} is proprietary software developed and owned by {appConfig.copyrightOwner}.{" "}
                        {b.name} is the installer responsible for delivering and supporting your renewable energy system.
                    </p>
                </div>

                <Section title="Copyright">
                    <div className="text-base text-[#131B2F] font-medium" data-testid="about-copyright-line">
                        {copyrightLine()}
                    </div>
                    <p className="mt-2 text-sm text-[#4B5565] leading-relaxed">{appConfig.copyrightNotice}</p>
                </Section>

                <Section title="Installer">
                    <div className="text-center py-2 space-y-1.5">
                        <div className="inline-flex bg-white rounded-md" data-testid="about-installer-logo">
                            <InstallerLogo height={48} />
                        </div>
                        <div className="text-lg text-[#131B2F] font-medium">{b.name}</div>
                        <div className="text-sm text-[#4B5565]">{b.tagline}</div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
                        <InstallerContactBlock />
                    </div>
                </Section>

                <Section title="Legal">
                    <LegalLink to="/legal/privacy"  label="Privacy Policy" testid="legal-privacy" />
                    <LegalLink to="/legal/terms"    label="Terms of Use"   testid="legal-terms" />
                    <LegalLink to="/legal/licences" label="Licences"       testid="legal-licences" last />
                </Section>

                <p className="mt-6 text-center text-xs text-[#4B5565]">
                    {versionLine()} · {copyrightLine()}
                </p>
            </PageContainer>
        </div>
    );
}

function Meta({ label, value }) {
    return (
        <div className="text-center">
            <div className="text-xs text-[#4B5565]">{label}</div>
            <div className="text-sm text-[#131B2F] font-medium mt-0.5">{value}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mt-6">
            <div className="text-[11px] text-[#4B5565] tracking-[0.14em] font-medium uppercase mb-2 px-1">{title}</div>
            <div className="bg-white rounded-[20px] border border-[#E5E7EB] p-6">{children}</div>
        </div>
    );
}

function LegalLink({ to, label, testid, last }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className={`flex items-center justify-between py-3 hover:text-[#3E9E2E] transition-colors ${!last ? "border-b border-[#E5E7EB]" : ""}`}
        >
            <span className="text-base text-[#131B2F]">{label}</span>
            <ChevronRight size={16} className="text-[#4B5565]" />
        </Link>
    );
}
